import { useState, type ChangeEvent, type JSX } from "react";

interface ConfirmDialogProps {
  title: string;
  description: string;
  confirmWord?: string;
  confirmPlaceholder?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending?: boolean;
  confirmLabel: string;
  cancelLabel: string;
}

export function ConfirmDialog({
  title,
  description,
  confirmWord,
  confirmPlaceholder,
  onConfirm,
  onCancel,
  isPending = false,
  confirmLabel,
  cancelLabel,
}: ConfirmDialogProps): JSX.Element {
  const [typedValue, setTypedValue] = useState("");
  const isTypedMode = Boolean(confirmWord);
  const canConfirm = isTypedMode ? typedValue === confirmWord : true;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      data-testid="confirm-dialog"
    >
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-[var(--sh-3)]">
        <h2 className="mb-2 text-lg font-semibold text-[var(--text-primary)]">{title}</h2>
        <p className="mb-4 text-sm text-[var(--text-subtle)]">{description}</p>

        {isTypedMode && (
          <input
            type="text"
            value={typedValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setTypedValue(e.target.value)}
            placeholder={confirmPlaceholder}
            disabled={isPending}
            data-testid="confirm-dialog-input"
            className="w-full rounded border border-[var(--border-default)] px-3 py-2 text-sm outline-none focus:border-[var(--intent-danger-500)] disabled:opacity-50"
          />
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            data-testid="confirm-dialog-cancel"
            className="rounded px-4 py-2 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--surface-sunken)] disabled:opacity-50 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm || isPending}
            data-testid="confirm-dialog-confirm"
            className="rounded bg-[var(--intent-danger-500)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--intent-danger-600)] disabled:opacity-50 transition-colors"
          >
            {isPending ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
